import { ListboxOptions } from '@headlessui/react'
import createPortal from '@mono/packages/ui/utils/createPortal'
import classnames from 'classnames'
import { identity, isEmpty, toLower } from 'ramda'
import React, { Fragment, useCallback, useMemo, useState } from 'react'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import SearchBox from '../../SearchBox'
import type { IDropdown } from '../Dropdown'
import DropdownListCreateOption from './DropdownListCreateOption'
import DropdownListNoDataOption from './DropdownListNoDataOption'
import DropdownListOption from './DropdownListOption'

interface IDropdownList extends IDropdown {
  popperStyles: { [key: string]: React.CSSProperties }
  popperAttributes: { [key: string]: { [key: string]: string } | undefined }
  setPopperElement: React.Ref<HTMLDivElement>
  onSearchChange?: (term: string) => void
  searchValue?: string
  loadMore?: () => void
  hasNextPage?: boolean
}

export const initialFilterState = ''

const DropdownList = ({
  value,
  data: propsData = [],
  dataParser = identity,
  textField = 'name',
  valueField = 'id',
  renderItem,
  allowCreate,
  createMinLength,
  createMaxLength,
  allowSearch = true,
  loading,
  listClass = 'max-h-[250px]',
  itemBorderClass,
  popperStyles,
  popperAttributes,
  popperPlacement,
  setPopperElement,
  renderListTop,
  renderItemSeparator,
  renderInPortal = false,
  onSearchChange,
  searchValue,
  loadMore,
  hasNextPage
}: IDropdownList) => {
  const [filter, setFilter] = useState(initialFilterState)

  const data = useMemo(
    () => dataParser(propsData || []),
    [propsData, dataParser]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      setFilter(value)
      onSearchChange?.(value)
    },
    [onSearchChange]
  )

  const observerRef = useInfiniteScroll({ loadMore, hasNextPage })

  if (loading && (!propsData || isEmpty(propsData)) && !searchValue) {
    return null
  }

  const list = (
    <ListboxOptions
      transition={!renderInPortal}
      ref={setPopperElement}
      style={popperStyles.popper}
      {...popperAttributes.popper}
      className={classnames([
        'nowheel absolute z-20 flex flex-col py-1 bg-white border border-oxford-gray-100 shadow-lg data-[closed]:scale-95 data-[closed]:opacity-0',
        listClass,
        {
          'origin-top transition duration-200 ease-out': !renderInPortal,
          'rounded-b-md': popperPlacement && popperPlacement !== 'top',
          'rounded-t-md': popperPlacement && popperPlacement === 'top',
          'w-full': !renderInPortal
        }
      ])}
    >
      <Fragment>
        {allowSearch && (
          <SearchBox
            value={searchValue ?? filter}
            onChange={handleSearchChange}
          />
        )}
        {renderListTop?.()}
        <div className="flex flex-col overflow-auto">
          {data && !isEmpty(data) ? (
            data.map((dataItem: Record<string, any>, index: number) => {
              if (
                [dataItem?.isActive, dataItem?.active].some(
                  (value) => typeof value === 'boolean' && !value
                )
              ) {
                return null
              }

              return (
                <React.Fragment key={dataItem[valueField]}>
                  {renderItemSeparator?.({
                    prevItem: data[index - 1] ?? null,
                    nextItem: dataItem
                  })}
                  <DropdownListOption
                    dataItem={dataItem}
                    textField={textField}
                    itemBorderClass={itemBorderClass}
                    valueField={valueField}
                    value={value}
                    renderItem={renderItem}
                    index={index}
                  />
                </React.Fragment>
              )
            })
          ) : (
            <DropdownListNoDataOption />
          )}

          {allowCreate &&
            !!filter.trim().length &&
            !data.find(
              (dataItem) =>
                toLower(dataItem[textField]) === toLower(filter.trim())
            ) && (
              <DropdownListCreateOption
                filter={filter}
                textField={textField}
                setFilter={setFilter}
                minLength={createMinLength}
                maxLength={createMaxLength}
              />
            )}

          {hasNextPage && <div ref={observerRef} className="h-px" />}
        </div>
      </Fragment>
    </ListboxOptions>
  )

  if (renderInPortal && typeof document !== 'undefined') {
    return createPortal(list)
  }

  return list
}

export default DropdownList
